import {
  AllTemplatesForBuilderDocument,
  AssemblyTemplateField,
  GetVersionsDocument,
  SortTypeEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TemplateTypeEnum,
  TextFormatEnum,
  UnitsEnum,
  useAddEditRolesToTemplateFieldMutation,
  useGetAllRolesQuery,
  useUpdateTemplateMutation,
} from '@/__generated__/graphql';
import { DropDownField } from '@/components/shared/CustomField';
import { containedButtonStyle } from '@/theme';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay, safeParseJson, upperSnakeCase } from '@/utils';
import { Add, AddCircle, CloudUpload, Delete, Edit } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import {
  Autocomplete,
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import Button from '../../shared/Button';
import {
  assemblyFields,
  selectedTemplateField as selectedAssemblyField,
  selectedTemplateId,
} from '../state';
import { useSnackBarMessage } from '../utils';
import { TemplateFields } from '../TemplateForm/shared';
import { BulkOptionsModal } from '../TemplateForm/BulkOptionsModal';
import { GenericDialog } from '../../shared/GenericDialog';
import {
  DropDownContainer,
  Field,
  FieldPanel,
  FieldsList,
  FormHeader,
  LineBreak,
  OptionBox,
  TemplateFieldContainer,
  dropDownStyles,
  tooltipStyles,
  AdditionalOptions,
  OptionChip,
} from '../TemplateForm/styles';
import { PageLoader } from '../../shared/PageLoader';
import { ColorSquare } from '../../Analysis/DeckPlan/ColorLegend/ColorLegend';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { MultiRangeOptions } from './MultiRangeOptions';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { prepareTemplateFields } from '@/hooks/usePrepareTemplateFields';

type EditField = {
  key: string;
  roles: string[];
  isEditable: boolean;
};

export const AssemblyFieldsPanel = ({
  emptyForm,
  structureId,
  handleEmptyForm,
}: {
  emptyForm: boolean;
  handleEmptyForm: (value: boolean) => void;
  structureId: string;
}) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const templateId = useAtomValue(selectedTemplateId)!;
  const [allTemplateFields, setAllTemplateFields] = useAtom(assemblyFields);
  const [selectedField, setSelectedField] = useAtom(selectedAssemblyField);
  const [metaJson, setMetaJson] = useState<string>('');
  const [metaError, setMetaError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setMetaJson(selectedField?.metaData ? JSON.stringify(selectedField.metaData, null, 2) : '');
    setMetaError(undefined);
  }, [selectedField?.metaData]);

  const [newOption, setNewOption] = useState<string>();
  const [newOptionColor, setNewOptionColor] = useState<string>();
  const [searchTerm, setSearchTerm] = useState('');
  const initialSelectedFieldRef = useRef<AssemblyTemplateField | undefined>(undefined);
  const userSelectedFieldRef = useRef<AssemblyTemplateField | undefined>(undefined);
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [enableColors, setEnableColors] = useState<boolean>(!!selectedField?.displayConfig?.color);
  const [selectedRoles, setSelectedRoles] = useState<EditField[]>([]);
  const [showDeleteFieldDialog, setShowDeleteFieldDialog] = useState<boolean>(false);
  const [rangesValid, setRangesValid] = useState<boolean>(true);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  useEffect(() => {
    if (initialSelectedFieldRef.current === undefined && selectedField) {
      initialSelectedFieldRef.current = selectedField;
    }
  }, [selectedField]);

  const filteredFields = useMemo(() => {
    if (!allTemplateFields) return [];
    const q = searchTerm.trim().toLowerCase();
    return allTemplateFields.filter((field) =>
      q ? (field.name ?? '').toLowerCase().includes(q) : true
    );
  }, [allTemplateFields, searchTerm]);

  useEffect(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) {
      if (userSelectedFieldRef.current) {
        const userSelected = userSelectedFieldRef.current;
        setSelectedField(
          userSelected.displayConfig ? userSelected : { ...userSelected, displayConfig: undefined }
        );
        setEnableColors(
          !!userSelected.displayConfig?.color &&
            (Array.isArray(userSelected.displayConfig.color)
              ? userSelected.displayConfig.color.length > 0
              : true)
        );
      } else if (initialSelectedFieldRef.current) {
        const orig = initialSelectedFieldRef.current;
        setSelectedField(orig.displayConfig ? orig : { ...orig, displayConfig: undefined });
        setEnableColors(
          !!orig.displayConfig?.color &&
            (Array.isArray(orig.displayConfig.color) ? orig.displayConfig.color.length > 0 : true)
        );
      }
      return;
    }

    if (filteredFields.length > 0) {
      const currentSelectedId = selectedField?.id;
      const currentFieldInFiltered = filteredFields.find((field) => field.id === currentSelectedId);

      if (currentFieldInFiltered) {
        return;
      }

      const first = filteredFields[0];
      setSelectedField(first.displayConfig ? first : { ...first, displayConfig: undefined });
      setEnableColors(
        !!first?.displayConfig?.color &&
          (Array.isArray(first.displayConfig.color) ? first.displayConfig.color.length > 0 : true)
      );
    }
  }, [searchTerm, filteredFields, setSelectedField, setEnableColors, selectedField?.id]);

  useEffect(() => {
    if (!allTemplateFields) return;

    const newFields = allTemplateFields
      .filter((field) => field.isEditable)
      .map((field) => ({
        key: field.key ?? '',
        roles: (field.editRoles ?? []).filter(
          (role): role is string => role !== null && role !== undefined
        ),
        isEditable: field.isEditable ?? false,
      }));

    if (newFields.length > 0) {
      setSelectedRoles((previous) => {
        const existingKeys = new Set(previous.map((item) => item.key));

        const filteredNewFields = newFields.filter((field) => !existingKeys.has(field.key));

        return filteredNewFields.length > 0 ? [...previous, ...filteredNewFields] : previous;
      });
    }
  }, [allTemplateFields, setSelectedRoles]);

  const updateField = useCallback(
    (field: AssemblyTemplateField | undefined) => {
      if (!field) {
        return;
      }
      setSelectedField(field);
      // Update the user selected field reference when a field is updated
      userSelectedFieldRef.current = field;
      setAllTemplateFields((previous) => {
        if (!previous) return undefined;
        return previous.map((templateField) => {
          if (templateField.id === field.id) return field;
          return templateField;
        });
      });
    },
    [setAllTemplateFields, setSelectedField]
  );

  const handleDeleteField = () => {
    if (selectedField) {
      // Store the current field ID for reference
      const deletedFieldId = selectedField.id;

      setAllTemplateFields((previous) => {
        if (!previous) return undefined;

        // Filter out the deleted field
        const updatedFields = previous.filter(
          (templateField) => templateField.id !== deletedFieldId
        );

        // If there are remaining fields, select the first one
        if (updatedFields.length > 0) {
          const nextField = updatedFields[0];
          // Schedule selection of the next field after state update
          setTimeout(() => {
            setSelectedField(
              nextField.displayConfig ? nextField : { ...nextField, displayConfig: undefined }
            );
            // Update the user selected field reference
            userSelectedFieldRef.current = nextField;
          }, 0);
        } else {
          // No fields left, clear selection
          setSelectedField(undefined);
          userSelectedFieldRef.current = undefined;
        }

        return updatedFields;
      });

      // Clear the initial selected field reference if it was the deleted field
      if (initialSelectedFieldRef.current?.id === deletedFieldId) {
        initialSelectedFieldRef.current = undefined;
      }

      setShowDeleteFieldDialog(false);
    }
  };

  const { data } = useGetAllRolesQuery({
    variables: {},
    fetchPolicy: 'cache-first',
  });

  const allRoles = useMemo(() => {
    return data?.allRoles.map((roles) => roles?.name) ?? [];
  }, [data?.allRoles]);

  const handleRolesSelection = useCallback(
    (value: string[]) => {
      setSelectedRoles((previous) => {
        const updatedRoles = value;
        const existingFieldIndex = previous.findIndex(
          (item) => item.key === (selectedField?.key ?? '')
        );

        if (existingFieldIndex === -1) {
          return [
            ...previous,
            {
              key: selectedField?.key ?? '',
              roles: updatedRoles,
              isEditable: selectedField?.isEditable ?? false,
            },
          ];
        } else {
          const updated = [...previous];
          updated[existingFieldIndex] = {
            ...updated[existingFieldIndex],
            roles: updatedRoles,
            isEditable: selectedField?.isEditable ?? updated[existingFieldIndex].isEditable,
          };
          return updated;
        }
      });
    },
    [selectedField?.isEditable, selectedField?.key, setSelectedRoles]
  );

  const handleRemoveOption = (option: string) => {
    if (selectedField) {
      const options = selectedField?.options?.filter((item) => item !== option) ?? [];
      updateField({ ...selectedField, options });
    }
  };

  const updateEditableField = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (selectedField) {
        updateField({ ...selectedField, isEditable: event.target.checked });
        setSelectedRoles((previous) =>
          previous.map((item) =>
            item.key === selectedField?.key ? { ...item, isEditable: event.target.checked } : item
          )
        );
      }
    },
    [selectedField, setSelectedRoles, updateField]
  );

  const [updateTemplateField] = useUpdateTemplateMutation({
    refetchQueries: [AllTemplatesForBuilderDocument],
    onCompleted: (response) => {
      if (!response?.updateTemplate) {
        showSnackBar('An error occurred while updating template', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const [updateEditRoles] = useAddEditRolesToTemplateFieldMutation({
    refetchQueries: [AllTemplatesForBuilderDocument],
    onCompleted: (response) => {
      if (!response?.addEditRolesToTemplateField) {
        showSnackBar('An error occurred while updating edit roles', 'error');
        return;
      }
      showSnackBar('Template saved successfully', 'success');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const handleSaveTemplate = useCallback(() => {
    if (selectedField) {
      const { cleanedFields, duplicateNewFields } = prepareTemplateFields(allTemplateFields);

      if (duplicateNewFields.length > 0) {
        showSnackBar('Remove duplicate fields to save template.', 'error');
        return;
      }

      updateTemplateField({
        variables: {
          input: {
            type: TemplateTypeEnum.Assembly,
            structureId,
            templateId,
            fields: cleanedFields,
            lastModifiedFor: 'Update Fields',
          },
        },
        refetchQueries: [GetVersionsDocument],
      }).then((response) => {
        if (!response.errors && response.data?.updateTemplate) {
          const isEditRolesEnabled = cleanedFields.some((field) => field.isEditable);
          if (isEditRolesEnabled && selectedRoles.length > 0) {
            updateEditRoles({
              variables: {
                input: {
                  structureId,
                  templateId: templateId ?? '',
                  type: TemplateTypeEnum.Assembly,
                  editRoles: selectedRoles.filter((field) => field.key !== ''),
                },
              },
              refetchQueries: [GetVersionsDocument],
            });
          }
        }
      });
    }
  }, [
    showSnackBar,
    allTemplateFields,
    selectedField,
    selectedRoles,
    structureId,
    templateId,
    updateEditRoles,
    updateTemplateField,
  ]);

  if (!selectedField) return <PageLoader />;

  return (
    <>
      {showOptionsModal && (
        <BulkOptionsModal
          open={showOptionsModal}
          enableColors={enableColors}
          handleUpdateWithColors={(newOptions, newColors) => {
            if (selectedField) {
              updateField({
                ...selectedField,
                options: [...(selectedField?.options ?? []), ...newOptions],
                displayConfig: selectedField.displayConfig
                  ? {
                      ...selectedField.displayConfig,
                      color: [...(selectedField.displayConfig?.color ?? []), ...newColors],
                    }
                  : selectedField.displayConfig,
              });
            }
          }}
          handleClose={() => {
            setShowOptionsModal(false);
          }}
        />
      )}
      {showDeleteFieldDialog && (
        <GenericDialog
          open={showDeleteFieldDialog}
          onClose={() => setShowDeleteFieldDialog(false)}
          title="Delete Field"
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              Are you sure you want to delete this field?
            </Typography>
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={() => setShowDeleteFieldDialog(false)}
                sx={{ color: abyssColors.primary[500] }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleDeleteField()}
                sx={containedButtonStyle}
              >
                Delete
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
      <Box sx={{ pt: 1, pb: 2 }}>
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search fields"
          variant="outlined"
          size="small"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: !!searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {filteredFields.length > 0 ? (
        <Stack>
          <FormHeader>
            {emptyForm ? (
              <TemplateFields
                value={''}
                placeholder="Template Name"
                mode={emptyForm ? 'Edit' : mode}
                onChange={(value) => {
                  setSelectedField({ ...selectedField, name: value });
                }}
              />
            ) : (
              <Typography fontSize="2.2rem" fontWeight={500}>
                {selectedField.name}
              </Typography>
            )}

            {mode === 'Edit' || emptyForm ? (
              <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
                <Button
                  variant="contained"
                  sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                  disabled={!rangesValid}
                  onClick={() => {
                    if (emptyForm) {
                      handleEmptyForm(false);
                    } else {
                      setMode('ReadOnly');
                      handleSaveTemplate();
                    }
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
                  onClick={() => {
                    setMode('ReadOnly');
                    handleEmptyForm(false);
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
                <Tooltip
                  title="Edit Template"
                  placement="top"
                  arrow
                  slotProps={{
                    popper: {
                      sx: { tooltipStyles },
                    },
                  }}
                >
                  <IconButton size="medium" onClick={() => setMode('Edit')}>
                    <Edit
                      fontSize="inherit"
                      sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </FormHeader>

          <LineBreak marginBottom={2} />
          <Stack direction="row" width="100%" gap={2}>
            <FieldsList>
              <Stack gap={1}>
                {filteredFields.map((field) => (
                  <Field
                    selected={selectedField?.id === field.id}
                    key={field.id}
                    onClick={() => {
                      const fieldToSelect = field.displayConfig
                        ? field
                        : {
                            ...field,
                            displayConfig: undefined,
                          };
                      setSelectedField(fieldToSelect);
                      // Store user's explicit selection
                      userSelectedFieldRef.current = fieldToSelect;
                      setEnableColors(
                        !!field?.displayConfig?.color && field?.displayConfig?.color.length > 0
                      );
                    }}
                  >
                    {field.name}
                  </Field>
                ))}
              </Stack>
              {(mode === 'Edit' || emptyForm) && (
                <Button
                  sx={{ marginTop: 2, height: '40px' }}
                  startIcon={<Add />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newField = {
                      name: '',
                      key: '',
                      options: [],
                      type: TemplateFieldTypeEnum.Input,
                      isReadOnly: true,
                      isRequired: true,
                      serverSide: true,
                      enableInlineEdit: true,
                      id: 'newField' + Date.now(),
                      displayConfig: {
                        id: generateMongoObjectId(),
                        type: TableCellStyleEnum.Text,
                        isSortable: false,
                        sortType: SortTypeEnum.String,
                        textAreaRows: undefined,
                        textFormat: TextFormatEnum.None,
                      },
                    };
                    setAllTemplateFields([...(allTemplateFields ?? []), newField]);
                    setSelectedField(newField);
                  }}
                >
                  Add Field
                </Button>
              )}
            </FieldsList>
            {selectedField && (
              <FieldPanel gap={3} direction="row">
                <Stack width={'50%'} gap={1}>
                  <Grid container maxWidth="60rem" ml={'-1.5rem'}>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.isRequired ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({
                              ...selectedField,
                              isRequired: event.target.checked,
                            });
                          }}
                        />
                        <Typography>Required</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.defaultVisible ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({ ...selectedField, defaultVisible: event.target.checked });
                          }}
                        />
                        <Typography>Default Visibility</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.isHidden ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({ ...selectedField, isHidden: event.target.checked });
                          }}
                        />
                        <Typography>Hidden</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.isReadOnly ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({ ...selectedField, isReadOnly: event.target.checked });
                          }}
                        />
                        <Typography>Read Only</Typography>
                      </Stack>
                    </Grid>

                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Tooltip
                          title="Select edit roles to make the field editable"
                          arrow
                          placement="top"
                        >
                          <span>
                            <Checkbox
                              checked={selectedField.isEditable ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => updateEditableField(event)}
                            />
                          </span>
                        </Tooltip>
                        <Typography>Editable</Typography>
                      </Stack>
                    </Grid>

                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <DropDownContainer>
                          <Autocomplete
                            fullWidth
                            multiple
                            disableCloseOnSelect
                            disabled={!selectedField.isEditable || mode === 'ReadOnly'}
                            options={allRoles}
                            value={
                              selectedRoles.find((item) => item.key === selectedField.key)?.roles ||
                              []
                            }
                            onChange={(event, newValue) =>
                              handleRolesSelection(newValue as string[])
                            }
                            renderInput={(parameters) => (
                              <TextField
                                {...parameters}
                                variant="standard"
                                label="Select Edit Roles"
                                placeholder="Select roles"
                                fullWidth
                              />
                            )}
                            getOptionLabel={(option) => option ?? ''}
                            renderOption={(props, option, { selected }) => (
                              <li {...props}>
                                <Checkbox checked={selected} style={{ marginRight: 8 }} />
                                {option}
                              </li>
                            )}
                            renderTags={(value, getTagProps) => {
                              const chipLimit = 2;
                              const additionalValues =
                                value.length > chipLimit
                                  ? value
                                      .slice(chipLimit)
                                      .map((item) => item)
                                      .join(', ')
                                  : '';
                              return (
                                <>
                                  {value.slice(0, chipLimit).map((option, index) => (
                                    <OptionChip
                                      {...getTagProps({ index })}
                                      label={option}
                                      key={option}
                                    />
                                  ))}
                                  <Tooltip
                                    title={additionalValues}
                                    arrow
                                    placement="bottom"
                                    sx={tooltipStyles}
                                  >
                                    <AdditionalOptions>
                                      {value.length > chipLimit && `+ ${value.length - chipLimit}`}
                                    </AdditionalOptions>
                                  </Tooltip>
                                </>
                              );
                            }}
                          />
                        </DropDownContainer>
                      </Stack>
                    </Grid>

                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.serverSide}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({ ...selectedField, serverSide: event.target.checked });
                          }}
                        />
                        <Typography>Server-side</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.displayConfig?.isSortable}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({
                              ...selectedField,
                              ...(selectedField.displayConfig
                                ? {
                                    displayConfig: {
                                      ...selectedField.displayConfig,
                                      isSortable: event.target.checked,
                                    },
                                  }
                                : {
                                    displayConfig: {
                                      id: generateMongoObjectId(),
                                      type: TableCellStyleEnum.Text,
                                      isSortable: event.target.checked,
                                      sortType: SortTypeEnum.String,
                                      textAreaRows: undefined,
                                      textFormat: TextFormatEnum.None,
                                    },
                                  }),
                            });
                          }}
                        />
                        <Typography>Sortable</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.displayInQuery ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({ ...selectedField, displayInQuery: event.target.checked });
                          }}
                        />
                        <Typography>Display in Query</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.displayInTable ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({ ...selectedField, displayInTable: event.target.checked });
                          }}
                        />
                        <Typography>Display in Table</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Tooltip
                          title="This field is currently only used for poi review panel fields. Use Review Panel Configuration tab instead"
                          arrow
                          placement="top"
                        >
                          <span>
                            <Checkbox
                              checked={selectedField.displayInReview ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => {
                                updateField({
                                  ...selectedField,
                                  displayInReview: event.target.checked,
                                });
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Typography>Display in Review</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={selectedField.displayInReportFilters ?? false}
                          disabled={mode === 'ReadOnly' && !emptyForm}
                          onChange={(event) => {
                            updateField({
                              ...selectedField,
                              displayInReportFilters: event.target.checked,
                            });
                          }}
                        />
                        <Typography>Display in Report Filters</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Tooltip
                          title="This field currently only handles es sheet columns to be exported"
                          arrow
                          placement="top"
                        >
                          <span>
                            <Checkbox
                              checked={selectedField.isExportable ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => {
                                updateField({
                                  ...selectedField,
                                  isExportable: event.target.checked,
                                });
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Typography>Exportable</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Tooltip
                          title="This field enables component table values edit"
                          arrow
                          placement="top"
                        >
                          <span>
                            <Checkbox
                              checked={selectedField.enableInlineEdit ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => {
                                updateField({
                                  ...selectedField,
                                  enableInlineEdit: event.target.checked,
                                });
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Typography>Enable Inline Edit</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={6}>
                      <Stack direction="row" alignItems="center">
                        <Tooltip
                          title="This field represent planner column"
                          arrow
                          placement="top"
                        >
                          <span>
                            <Checkbox
                              checked={selectedField.isPlannerColumn ?? false}
                              disabled={mode === 'ReadOnly' && !emptyForm}
                              onChange={(event) => {
                                updateField({
                                  ...selectedField,
                                  isPlannerColumn: event.target.checked,
                                });
                              }}
                            />
                          </span>
                        </Tooltip>
                        <Typography>Planner Column</Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                  {(mode === 'Edit' || emptyForm) && (
                    <TemplateFields
                      label="Name"
                      value={selectedField.name}
                      mode={emptyForm ? 'Edit' : mode}
                      onChange={(value) => {
                        updateField({
                          ...selectedField,
                          name: value,
                        });
                      }}
                    />
                  )}

                  <TemplateFields
                    label="Key"
                    value={selectedField.key}
                    onChange={(value) => {
                      updateField({
                        ...selectedField,
                        key: value || selectedField.name || selectedField.id,
                      });
                    }}
                    mode={selectedField.id.includes('newField') ? mode : 'ReadOnly'}
                  />

                  <TemplateFields
                    label="Detail Field Key"
                    value={selectedField.detailFieldKey}
                    onChange={(value) => {
                      updateField({
                        ...selectedField,
                        detailFieldKey: value,
                      });
                    }}
                    mode={mode}
                  />

                  <TemplateFields
                    label="Permission"
                    value={selectedField.permission}
                    mode={emptyForm ? 'Edit' : mode}
                    onChange={(value) => {
                      updateField({
                        ...selectedField,
                        permission: value || undefined,
                      });
                    }}
                  />
                  <DropDownContainer>
                    <Typography>Field Type</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.type) || ''}
                      options={Object.values(TemplateFieldTypeEnum).map((value) =>
                        formatEnumForDisplay(value)
                      )}
                      onFieldValueChange={(value) => {
                        if (value && selectedField) {
                          const type =
                            Object.values(TemplateFieldTypeEnum).find(
                              (enumValue) => enumValue === upperSnakeCase(value)
                            ) ?? TemplateFieldTypeEnum.Input;
                          updateField({
                            ...selectedField,
                            type: type,
                          });
                        }
                      }}
                    />
                  </DropDownContainer>
                  {selectedField.displayConfig?.isSortable && (
                    <Stack
                      direction="row"
                      gap={1}
                      alignItems="center"
                      width="100%"
                      maxWidth="50rem"
                      justifyContent="space-between"
                    >
                      <Typography>Sort Type</Typography>
                      <DropDownField
                        mode={emptyForm ? 'Edit' : mode}
                        id={selectedField.id}
                        title=""
                        sx={dropDownStyles}
                        disableClearable
                        value={formatEnumForDisplay(selectedField.displayConfig.sortType) || ''}
                        options={Object.values(SortTypeEnum).map((value) =>
                          formatEnumForDisplay(value)
                        )}
                        onFieldValueChange={(value) => {
                          if (value) {
                            const sortType = Object.values(SortTypeEnum).find(
                              (enumValue) => enumValue === upperSnakeCase(value)
                            );
                            updateField({
                              ...selectedField,
                              ...(selectedField.displayConfig
                                ? {
                                    displayConfig: {
                                      ...selectedField.displayConfig,
                                      ...(sortType
                                        ? { sortType }
                                        : { sortType: SortTypeEnum.String }),
                                    },
                                  }
                                : {
                                    displayConfig: {
                                      id: generateMongoObjectId(),
                                      type: TableCellStyleEnum.Text,
                                      isSortable: false,
                                      ...(sortType
                                        ? { sortType }
                                        : { sortType: SortTypeEnum.String }),
                                      textAreaRows: undefined,
                                    },
                                  }),
                            });
                          }
                        }}
                      />
                    </Stack>
                  )}

                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    width="100%"
                    maxWidth="50rem"
                    justifyContent="space-between"
                  >
                    <Typography>Text Format</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.displayConfig?.textFormat) || ''}
                      options={Object.values(TextFormatEnum).map((value) =>
                        formatEnumForDisplay(value)
                      )}
                      onFieldValueChange={(value) => {
                        if (value) {
                          const textFormat = Object.values(TextFormatEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          );
                          updateField({
                            ...selectedField,
                            ...(selectedField.displayConfig
                              ? {
                                  displayConfig: {
                                    ...selectedField.displayConfig,
                                    ...(textFormat
                                      ? { textFormat }
                                      : { textFormat: TextFormatEnum.None }),
                                  },
                                }
                              : {
                                  displayConfig: {
                                    id: generateMongoObjectId(),
                                    type: TableCellStyleEnum.Text,
                                    isSortable: false,
                                    sortType: SortTypeEnum.String,
                                    ...(textFormat
                                      ? { textFormat }
                                      : { textFormat: TextFormatEnum.None }),
                                    textAreaRows: undefined,
                                  },
                                }),
                          });
                        }
                      }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    width="100%"
                    maxWidth="50rem"
                    justifyContent="space-between"
                  >
                    <Typography>Unit</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.unit) || ''}
                      options={Object.values(UnitsEnum).map((value) => formatEnumForDisplay(value))}
                      onFieldValueChange={(value) => {
                        if (value) {
                          const unit = Object.values(UnitsEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          );
                          updateField({
                            ...selectedField,
                            unit,
                          });
                        }
                      }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="center"
                    width="100%"
                    maxWidth="50rem"
                    justifyContent="space-between"
                  >
                    <Typography>Display Unit</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.displayUnit) || ''}
                      options={Object.values(UnitsEnum).map((value) => formatEnumForDisplay(value))}
                      onFieldValueChange={(value) => {
                        if (value) {
                          const displayUnit = Object.values(UnitsEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          );
                          updateField({
                            ...selectedField,
                            displayUnit,
                          });
                        }
                      }}
                    />
                  </Stack>

                  {selectedField.type === TemplateFieldTypeEnum.Numeric && (
                    <TemplateFields
                      label="Decimal Places"
                      value={selectedField.roundToDecimal?.toString()}
                      mode={emptyForm ? 'Edit' : mode}
                      type={TemplateFieldTypeEnum.Numeric}
                    />
                  )}
                  {selectedField.type === TemplateFieldTypeEnum.Textarea && (
                    <TemplateFields
                      label="Text Area Rows"
                      value={selectedField.displayConfig?.textAreaRows?.toString()}
                      mode={emptyForm ? 'Edit' : mode}
                      type={TemplateFieldTypeEnum.Numeric}
                    />
                  )}
                  <DropDownContainer>
                    <Typography>Display Type</Typography>
                    <DropDownField
                      mode={emptyForm ? 'Edit' : mode}
                      id={selectedField.id}
                      title=""
                      sx={dropDownStyles}
                      disableClearable
                      value={formatEnumForDisplay(selectedField.displayConfig?.type) || ''}
                      options={Object.values(TableCellStyleEnum).map((value) =>
                        formatEnumForDisplay(value)
                      )}
                      onFieldValueChange={(value) => {
                        if (value) {
                          const type = Object.values(TableCellStyleEnum).find(
                            (enumValue) => enumValue === upperSnakeCase(value)
                          );
                          updateField({
                            ...selectedField,
                            ...(selectedField.displayConfig
                              ? {
                                  displayConfig: {
                                    ...selectedField.displayConfig,
                                    ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                  },
                                }
                              : {
                                  displayConfig: {
                                    id: generateMongoObjectId(),
                                    ...(type ? { type } : { type: TableCellStyleEnum.Text }),
                                    isSortable: false,
                                    sortType: SortTypeEnum.String,
                                    inputAdorment: undefined,
                                    textAreaRows: undefined,
                                    textFormat: TextFormatEnum.None,
                                  },
                                }),
                          });
                        }
                      }}
                    />
                  </DropDownContainer>
                  <Stack direction="row" pt={2} spacing={2} alignItems="center">
                    <MultiRangeOptions
                      fieldKey={selectedField.id}
                      mode={emptyForm ? 'Edit' : mode}
                      optionsData={selectedField.optionsData ?? []}
                      onChange={(value) => {
                        updateField({
                          ...selectedField,
                          optionsData: value,
                        });
                      }}
                      onValidityChange={setRangesValid}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center"></Stack>
                  <LineBreak marginY={4} />
                  {(mode === 'Edit' || emptyForm) && (
                    <Button
                      sx={{ width: '50%', height: '40px' }}
                      startIcon={<Delete />}
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowDeleteFieldDialog(true);
                      }}
                    >
                      Remove Field
                    </Button>
                  )}
                </Stack>
                {(selectedField.type === TemplateFieldTypeEnum.MultiSelect ||
                  selectedField.type === TemplateFieldTypeEnum.SingleSelect ||
                  selectedField.type === TemplateFieldTypeEnum.RadioButton ||
                  selectedField?.metaData) && (
                  <Stack sx={{ width: '50%' }} gap={2}>
                    {selectedField?.options && selectedField?.options.length > 0 && (
                      <>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography fontWeight={500} fontSize="1.6rem">
                            Options
                          </Typography>
                          <Typography variant="subtitle2">
                            ({selectedField?.options?.length})
                          </Typography>
                        </Stack>
                        <TemplateFieldContainer>
                          {selectedField?.options?.map((option, index) => (
                            <OptionBox key={`${option}-${index}`}>
                              <Typography>{option}</Typography>
                              <Stack direction="row" gap={2}>
                                {enableColors && selectedField.displayConfig?.color && (
                                  <ColorSquare
                                    sx={{
                                      width: '1.6rem',
                                      height: '1.6rem',
                                      border: `1px solid ${abyssColors.neutral[300]}`,
                                    }}
                                    color={
                                      selectedField.displayConfig?.color.find(
                                        (color) => color?.key === option
                                      )?.value ?? 'transparent'
                                    }
                                  />
                                )}
                                {(mode === 'Edit' || emptyForm) &&
                                  selectedField.id.includes('newField') && (
                                    <Delete
                                      onClick={() => {
                                        handleRemoveOption(option);
                                      }}
                                    />
                                  )}
                              </Stack>
                            </OptionBox>
                          ))}
                        </TemplateFieldContainer>

                        {(mode === 'Edit' || emptyForm) &&
                          selectedField.id.includes('newField') && (
                            <>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                justifyContent="space-between"
                              >
                                <Stack direction="row" alignItems="center" gap={1}>
                                  <Checkbox
                                    disableRipple
                                    sx={{ padding: 0 }}
                                    checked={enableColors ?? false}
                                    disabled={!selectedField.id.includes('newField')}
                                    onChange={(event) => {
                                      setEnableColors(event.target.checked);
                                      updateField({
                                        ...selectedField,
                                        ...(selectedField.displayConfig
                                          ? {
                                              displayConfig: {
                                                ...selectedField.displayConfig,
                                                color: event.target.checked ? [] : undefined,
                                              },
                                            }
                                          : {
                                              displayConfig: {
                                                id: generateMongoObjectId(),
                                                type: TableCellStyleEnum.Text,
                                                isSortable: false,
                                                sortType: SortTypeEnum.String,
                                                textAreaRows: undefined,
                                                color: event.target.checked ? [] : undefined,
                                              },
                                            }),
                                      });
                                    }}
                                  />
                                  <Typography>Option Colors</Typography>
                                </Stack>
                                <Typography color={abyssColors.neutral[500]}>
                                  Unchecking this will remove existing colors from all options for
                                  this field
                                </Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                gap={2}
                                marginBottom={2}
                                width="100%"
                                alignItems="center"
                              >
                                <Stack width="100%">
                                  <TemplateFields
                                    label="Option Name"
                                    value={newOption}
                                    onChange={(value) => {
                                      if (value) {
                                        setNewOption(value);
                                      }
                                    }}
                                    mode={emptyForm ? 'Edit' : mode}
                                  />
                                  {enableColors && (
                                    <Stack direction="row" alignItems="center" gap={1}>
                                      <TemplateFields
                                        label="Option Color"
                                        value={newOptionColor}
                                        onChange={(value) => {
                                          if (value) {
                                            setNewOptionColor(value);
                                          }
                                        }}
                                        mode={emptyForm ? 'Edit' : mode}
                                        endAdornment="color"
                                      />
                                    </Stack>
                                  )}
                                </Stack>
                                <Button
                                  sx={{ width: '20rem', height: '32px' }}
                                  startIcon={<AddCircle />}
                                  variant="outlined"
                                  size="small"
                                  disabled={!newOption}
                                  onClick={() => {
                                    if (newOption) {
                                      updateField({
                                        ...selectedField,
                                        options: [...(selectedField?.options ?? []), newOption],
                                        displayConfig:
                                          selectedField.displayConfig && newOptionColor
                                            ? {
                                                ...selectedField.displayConfig,
                                                color: [
                                                  ...(selectedField.displayConfig?.color ?? []),
                                                  {
                                                    key: newOption,
                                                    value: newOptionColor,
                                                  },
                                                ],
                                              }
                                            : selectedField.displayConfig,
                                      });
                                    }
                                    setNewOption('');
                                    setNewOptionColor('');
                                  }}
                                >
                                  Add New Option
                                </Button>
                              </Stack>
                              <Button
                                sx={{ width: '50%', height: '40px', ml: 'auto' }}
                                startIcon={<CloudUpload />}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setShowOptionsModal(true);
                                }}
                              >
                                Add options from file
                              </Button>
                            </>
                          )}
                        {(mode === 'Edit' || emptyForm) &&
                          !selectedField.id.includes('newField') && (
                            <Typography textAlign="center" mt={2} color={abyssColors.neutral[400]}>
                              Editing options for existing fields is not allowed
                            </Typography>
                          )}
                        <Typography color={abyssColors.neutral[500]}>
                          <Typography fontWeight={600}>Note:</Typography> To enable filtering, the
                          field must be made editable, as the data is not yet present on the
                          assemblies.
                        </Typography>
                      </>
                    )}

                    {selectedField?.metaData && (
                      <TemplateFieldContainer>
                        <Typography fontWeight={500} fontSize="1.6rem">
                          MetaData
                        </Typography>
                        <Stack gap={2}>
                          <TextField
                            multiline
                            minRows={6}
                            fullWidth
                            size="small"
                            value={metaJson}
                            disabled={!(mode === 'Edit' || emptyForm)}
                            onChange={(e) => {
                              setMetaJson(e.target.value);
                              setMetaError(undefined);
                            }}
                            error={!!metaError}
                            helperText={metaError}
                          />

                          {(mode === 'Edit' || emptyForm) && (
                            <Stack direction="row" gap={2}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  const { value: parsed, error } = safeParseJson(metaJson || '{}');
                                  if (error) {
                                    setMetaError(error);
                                    showSnackBar(error, 'error');
                                    return;
                                  }
                                  updateField({ ...selectedField, metaData: parsed });
                                  setMetaError(undefined);
                                  showSnackBar('JSON applied successfully', 'success');
                                }}
                              >
                                Apply JSON
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setMetaJson(
                                    selectedField.metaData
                                      ? JSON.stringify(selectedField.metaData, null, 2)
                                      : ''
                                  );
                                  setMetaError(undefined);
                                }}
                              >
                                Reset
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                      </TemplateFieldContainer>
                    )}
                  </Stack>
                )}
              </FieldPanel>
            )}
          </Stack>
        </Stack>
      ) : (
        <Box sx={{ pt: 2 }}>
          <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
            <EmptyStatePlaceholder type="template" message="No Results Found" />
          </FieldPanel>
        </Box>
      )}
    </>
  );
};
